import axios from './axiosConfig';
import { setAuthToken } from './auth';

/**
 * Shared push-subscription logic for the customer app.
 *
 * Scoping note: a subscription belongs to a user + device, not a vendor.
 * `push_subscriptions` has no vendor_id - vendors are targeted at send time by
 * joining through enrollments. On Android/desktop every vendor PWA on this
 * origin shares one service worker registration, and therefore one
 * subscription. On iOS each Home Screen app is sandboxed and gets its own.
 */

export const getVapidKey = () => process.env.REACT_APP_VAPID_PUBLIC_KEY;

export const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export const isPushSupported = () =>
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  typeof Notification !== 'undefined';

const bytesEqual = (a, b) => {
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

// Treat a VAPID key mismatch as a dead subscription.
//
// Safe to enable, verified two ways on 18 Aug 2026:
//  1. VAPID_PUBLIC_KEY is identical in the staging and production EB consoles,
//     and both workflows build from the same repo-level GitHub secret (neither
//     declares an `environment:`), so every build agrees with every backend.
//  2. Push delivery works end to end, which proves it independently - a push
//     service rejects with 403 when the VAPID JWT's public key does not match
//     the subscription's applicationServerKey.
//
// Set back to false if the keys are ever rotated out of step - with a genuine
// mismatch this would unsubscribe and re-subscribe every customer on their next
// visit. The other repair paths (expiry, server dropped the row) do not depend
// on this flag.
const ENABLE_VAPID_KEY_REPAIR = true;

// At most one repair check per 10 minutes per page session.
const REPAIR_THROTTLE_MS = 10 * 60 * 1000;
let lastRepairAt = 0;

/**
 * A subscription is "stale" when it can no longer receive our pushes:
 *  - it has expired, or
 *  - it was created with a different VAPID key (e.g. the key was rotated),
 *    in which case our server can never successfully push to it again.
 */
export const isSubscriptionStale = (subscription) => {
  if (!subscription) return false;

  if (subscription.expirationTime && subscription.expirationTime <= Date.now()) {
    return true;
  }

  if (!ENABLE_VAPID_KEY_REPAIR) return false;

  const vapidKey = getVapidKey();
  const current = subscription.options?.applicationServerKey;
  if (!vapidKey || !current) return false;

  try {
    const mismatch = !bytesEqual(new Uint8Array(current), urlBase64ToUint8Array(vapidKey));
    if (mismatch) {
      console.warn('[push] Subscription was created with a different VAPID key');
    }
    return mismatch;
  } catch (e) {
    // If we cannot compare, assume it is fine rather than churning subscriptions.
    return false;
  }
};

const applyAuth = () => {
  const token = localStorage.getItem('token');
  if (token) setAuthToken(token);
  return Boolean(token);
};

export const sendSubscriptionToServer = async (subscription) => {
  applyAuth();
  await axios.post('/api/push-subscriptions/subscribe', { subscription });
};

/** Does the server still have this endpoint for the current user? */
const isKnownToServer = async (endpoint) => {
  try {
    applyAuth();
    const response = await axios.get('/api/push-subscriptions/status', {
      params: { endpoint }
    });
    return response.data?.known === true;
  } catch (err) {
    // On any failure, assume it IS known so we never churn a good subscription
    // just because the status check was unreachable.
    console.warn('[push] Status check failed, assuming subscription is known:', err);
    return true;
  }
};

const createSubscription = async (registration) => {
  const vapidKey = getVapidKey();
  if (!vapidKey) throw new Error('REACT_APP_VAPID_PUBLIC_KEY is not configured');

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidKey)
  });
};

/**
 * Inspect the current subscription without changing anything.
 * Returns { supported, permission, subscription, stale }.
 */
export const inspectSubscription = async () => {
  if (!isPushSupported()) {
    return { supported: false, permission: 'unsupported', subscription: null, stale: false };
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  return {
    supported: true,
    permission: Notification.permission,
    subscription,
    stale: isSubscriptionStale(subscription)
  };
};

/**
 * Repair the subscription if needed, without ever prompting the user.
 *
 * Handles the three ways a subscription silently dies:
 *   1. expired, or created with an old VAPID key  -> replace it
 *   2. the server dropped the row (pushService deletes on a 410) while the
 *      browser kept its subscription               -> replace it
 *   3. the server simply never received it         -> re-post it
 *
 * Only runs when permission is already granted, so it can never trigger a
 * browser dialog. Returns { state, subscription } where state is one of
 * 'unsupported' | 'no-permission' | 'none' | 'ok' | 'renewed' | 'failed'.
 */
export const repairSubscription = async ({ force = false } = {}) => {
  if (!isPushSupported()) return { state: 'unsupported', subscription: null };
  if (Notification.permission !== 'granted') {
    return { state: 'no-permission', subscription: null };
  }

  // The inbox calls this on every open. Without a throttle that is a status
  // GET plus a subscribe POST each time, for no benefit - a subscription does
  // not die twice in a minute.
  if (!force && lastRepairAt && Date.now() - lastRepairAt < REPAIR_THROTTLE_MS) {
    return { state: 'ok', subscription: null, throttled: true };
  }
  lastRepairAt = Date.now();

  try {
    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) return { state: 'none', subscription: null };

    // 1. Locally detectable death: expired or wrong VAPID key.
    if (isSubscriptionStale(subscription)) {
      console.log('[push] Subscription is stale, renewing');
      await subscription.unsubscribe().catch(() => {});
      subscription = await createSubscription(registration);
      await sendSubscriptionToServer(subscription);
      return { state: 'renewed', subscription };
    }

    // 2. Server-side death: the row was removed after a 410 from the push
    //    service, but this browser still reports a subscription.
    const known = await isKnownToServer(subscription.endpoint);
    if (!known) {
      console.log('[push] Server does not know this endpoint, renewing');
      await subscription.unsubscribe().catch(() => {});
      subscription = await createSubscription(registration);
      await sendSubscriptionToServer(subscription);
      return { state: 'renewed', subscription };
    }

    // 3. Healthy - keep the server in step (upsert, so this is cheap).
    await sendSubscriptionToServer(subscription);
    return { state: 'ok', subscription };
  } catch (err) {
    console.error('[push] Repair failed:', err);
    return { state: 'failed', subscription: null, error: err };
  }
};

/**
 * Ask permission (if needed) and subscribe. Must be called from a user gesture
 * - iOS in particular will not show the permission dialog otherwise.
 *
 * Returns { state, subscription } where state is one of
 * 'subscribed' | 'denied' | 'dismissed' | 'unsupported' | 'failed'.
 */
export const subscribeToPush = async () => {
  if (!isPushSupported()) return { state: 'unsupported' };

  try {
    const permission = await Notification.requestPermission();

    if (permission === 'denied') return { state: 'denied' };
    // Some browsers report 'default' when the dialog is dismissed without a choice.
    if (permission !== 'granted') return { state: 'dismissed' };

    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();

    // Replace anything unusable rather than reusing it.
    if (subscription && isSubscriptionStale(subscription)) {
      await subscription.unsubscribe().catch(() => {});
      subscription = null;
    }
    if (!subscription) {
      subscription = await createSubscription(registration);
    }

    await sendSubscriptionToServer(subscription);
    return { state: 'subscribed', subscription };
  } catch (err) {
    console.error('[push] Subscribe failed:', err);
    if (err?.name === 'NotAllowedError') return { state: 'denied' };
    return { state: 'failed', error: err };
  }
};

/** Unsubscribe locally and on the server. */
export const unsubscribeFromPush = async () => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
    }
    applyAuth();
    await axios.post('/api/push-subscriptions/unsubscribe');
    return { state: 'unsubscribed' };
  } catch (err) {
    console.error('[push] Unsubscribe failed:', err);
    return { state: 'failed', error: err };
  }
};
